import { DeepPartial, DeepReadonly } from 'ts-essentials';
import { ICTTextParagraphProperties, STTextTabAlignType } from '@tencent/slide-sdk-model';
import { IParagraphStyle } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-styles.interface';
import { TEXT_ALIGN_TYPE_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/text-align-type';
import { PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO, emuToPt, numToPercent, percentToNum, ptToEmu } from '@src/slide/infrastructure/module/independent/unit';
import { TEXT_TAB_ALIGN_TYPE_MAP } from '@src/slide/domain/ui-model/converter/node-property/sdk-ui-enum-map/text-tab-align-type';
import _set from 'lodash/set';
import { TextRunStyleConverter } from '@src/slide/domain/ui-model/converter/node-property/module-property/text/text-run/text-run-style-converter';
import { invertMap } from '@src/slide/infrastructure/module/independent/object/map-invert';

/**
 * 文本段落属性 - 基本属性
 */
export class TextParagraphPropsConverter {
    /**
     * 文本段落属性的sdk -> ui 映射
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        // level（用以匹配styleList）
        TextParagraphPropsConverter.convertLevelSdkToUi(sdkData, uiData);
        // 对齐方式
        TextParagraphPropsConverter.convertTextAlignSdkToUi(sdkData, uiData);
        // 缩进 - 文本之前
        TextParagraphPropsConverter.convertMarginLeftSdkToUi(sdkData, uiData);
        // 缩进 - 特殊格式
        TextParagraphPropsConverter.convertIndentSdkToUi(sdkData, uiData);
        // 间距（段前、段后、行间距）
        TextParagraphPropsConverter.convertLineSpaceSdkToUi(sdkData, uiData);
        // 默认制表位
        TextParagraphPropsConverter.convertTabSdkToUi(sdkData, uiData);
        // 自定义制表位列表
        TextParagraphPropsConverter.convertTabListSdkToUi(sdkData, uiData);
        // defaultRunProps
        TextParagraphPropsConverter.convertDefaultRunPropsSdkToUi(sdkData, uiData);
    }

    /**
     * 文本段落属性的ui -> sdk 映射
     * @param uiData
     * @param sdkData
     */
    public static convertPartialDataFromUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        // level（用以匹配styleList）
        TextParagraphPropsConverter.convertLevelUiToSdk(uiData, sdkData);
        // 对齐方式
        TextParagraphPropsConverter.convertTextAlignUiToSdk(uiData, sdkData);
        // 缩进 - 文本之前
        TextParagraphPropsConverter.convertMarginLeftUiToSdk(uiData, sdkData);
        // 缩进 - 特殊格式
        TextParagraphPropsConverter.convertIndentUiToSdk(uiData, sdkData);
        // 间距（段前、段后、行间距）
        TextParagraphPropsConverter.convertLineSpaceUiToSdk(uiData, sdkData);
        // 默认制表位
        TextParagraphPropsConverter.convertTabUiToSdk(uiData, sdkData);
        // 自定义制表位列表
        TextParagraphPropsConverter.convertTabListUiToSdk(uiData, sdkData);
        // defaultRunProps
        TextParagraphPropsConverter.convertDefaultRunPropsUiToSdk(uiData, sdkData);
    }

    /**
     * level
     * @param sdkData
     * @param uiData
     */
    private static convertLevelSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.lvl || sdkData.lvl === 0) {
            uiData.level = sdkData.lvl;
        } else {
            delete uiData.level;
        }
    }

    /**
     * 对齐方式
     * @param sdkData
     * @param uiData
     */
    private static convertTextAlignSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.algn) {
            uiData.textAlign = TEXT_ALIGN_TYPE_MAP.get(sdkData.algn);
        } else {
            delete uiData.textAlign;
        }
    }

    /**
     * 缩进 - 文本之前
     * @param sdkData
     * @param uiData
     */
    private static convertMarginLeftSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.marL || sdkData.marL === 0) {
            uiData.marginLeft = emuToPt(sdkData.marL);
        } else {
            delete uiData.marginLeft;
        }
    }

    /**
     * 缩进 - 特殊格式
     * @param sdkData
     * @param uiData
     */
    private static convertIndentSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.indent || sdkData.indent === 0) {
            uiData.textIndent = emuToPt(sdkData.indent);
        } else {
            delete uiData.textIndent;
        }
    }

    /**
     * 间距
     * @param sdkData
     * @param uiData
     */
    // eslint-disable-next-line complexity
    private static convertLineSpaceSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        // 段前
        if (sdkData.spcBef?.spcPts?.val || sdkData.spcBef?.spcPts?.val === 0) {
            uiData.paddingTop = sdkData.spcBef.spcPts.val / 100;
            uiData.paddingTopIs = 'absolute';
        }

        if (sdkData.spcBef?.spcPct?.val || sdkData.spcBef?.spcPct?.val === 0) {
            uiData.paddingTop = percentToNum(sdkData.spcBef.spcPct.val);
            uiData.paddingTopIs = 'percent';
        }

        // 段后
        if (sdkData.spcAft?.spcPts?.val || sdkData.spcAft?.spcPts?.val === 0) {
            uiData.paddingBottom = sdkData.spcAft.spcPts.val / 100;
            uiData.paddingBottomIs = 'absolute';
        }

        if (sdkData.spcAft?.spcPct?.val || sdkData.spcAft?.spcPct?.val === 0) {
            uiData.paddingBottom = percentToNum(sdkData.spcAft.spcPct.val);
            uiData.paddingBottomIs = 'percent';
        }

        // 行间距
        if (sdkData.lnSpc?.spcPct?.val || sdkData.lnSpc?.spcPct?.val === 0) {
            uiData.PPTLineHeight = percentToNum(sdkData.lnSpc.spcPct.val);
            uiData.lineHeight = uiData.PPTLineHeight * PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO;
            uiData.lineHeightIs = 'percent';
        }
        if (sdkData.lnSpc?.spcPts?.val || sdkData.lnSpc?.spcPts?.val === 0) {
            uiData.PPTLineHeight = undefined;
            uiData.lineHeight = sdkData.lnSpc.spcPts.val / 100;
            uiData.lineHeightIs = 'absolute';
        }
    }

    /**
     * 默认制表位
     * @param sdkData
     * @param uiData
     */
    private static convertTabSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.defTabSz || sdkData.defTabSz === 0) {
            uiData.tabSize = emuToPt(sdkData.defTabSz);
        } else {
            delete uiData.tabSize;
        }
    }

    /**
     * 自定义制表位列表
     * @param sdkData
     * @param uiData
     */
    private static convertTabListSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.tabLst?.tab && Array.isArray(sdkData.tabLst.tab)) {
            // @ts-ignore
            uiData.tabList = sdkData.tabLst.tab.map(tab => ({
                position: emuToPt(tab.pos),
                alignment: TEXT_TAB_ALIGN_TYPE_MAP.get(tab.algn),
            }));
        } else {
            delete uiData.tabList;
        }
    }

    /**
     * defaultRunProps
     * @param sdkData
     * @param uiData
     */
    private static convertDefaultRunPropsSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ICTTextParagraphProperties>>,
        uiData: IParagraphStyle,
    ): void {
        if (sdkData.defRPr) {
            uiData.defaultRunProps = {};
            TextRunStyleConverter.convertPartialDataFromSdkToUi(sdkData.defRPr, uiData.defaultRunProps);
        } else {
            delete uiData.defaultRunProps;
        }
    }

    /**
     * level
     * @param uiData
     * @param sdkData
     */
    private static convertLevelUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.level || uiData.level === 0) {
            sdkData.lvl = uiData.level;
        }
    }

    /**
     * 对齐方式
     * @param uiData
     * @param sdkData
     */
    private static convertTextAlignUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.textAlign) {
            sdkData.algn = Number(invertMap(TEXT_ALIGN_TYPE_MAP).get(uiData.textAlign));
        }
    }

    /**
     * 缩进 - 文本之前
     * @param uiData
     * @param sdkData
     */
    private static convertMarginLeftUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.marginLeft || uiData.marginLeft === 0) {
            sdkData.marL = ptToEmu(uiData.marginLeft);
        }
    }

    /**
     * 缩进 - 特殊格式
     * @param uiData
     * @param sdkData
     */
    private static convertIndentUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.textIndent || uiData.textIndent === 0) {
            sdkData.indent = ptToEmu(uiData.textIndent);
        }
    }

    /**
     * 间距
     * @param uiData
     * @param sdkData
     */
    // eslint-disable-next-line complexity
    private static convertLineSpaceUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        // 段前
        if (uiData.paddingTop || uiData.paddingTop === 0) {
            if (uiData.paddingTopIs === 'absolute') {
                _set(sdkData, 'spcBef.spcPts.val', uiData.paddingTop * 100);
            } else {
                _set(sdkData, 'spcBef.spcPct.val', numToPercent(uiData.paddingTop));
            }
        }
        // 段后
        if (uiData.paddingBottom || uiData.paddingBottom === 0) {
            if (uiData.paddingBottomIs === 'absolute') {
                _set(sdkData, 'spcAft.spcPts.val', uiData.paddingBottom * 100);
            } else {
                _set(sdkData, 'spcAft.spcPct.val', numToPercent(uiData.paddingBottom));
            }
        }
        // 行间距
        if (uiData.lineHeight || uiData.lineHeight === 0) {
            if (uiData.lineHeightIs === 'absolute') {
                _set(sdkData, 'lnSpc.spcPts.val', uiData.lineHeight * 100);
            } else {
                if (uiData.PPTLineHeight || uiData.PPTLineHeight === 0) {
                    _set(sdkData, 'lnSpc.spcPct.val', numToPercent(uiData.PPTLineHeight));
                } else {
                    _set(sdkData, 'lnSpc.spcPct.val', numToPercent(uiData.lineHeight / PERCENT_LINE_HEIGHT_SLIDE_TO_PPT_RATIO));
                }
            }
        }
    }

    /**
     * 默认制表位
     * @param uiData
     * @param sdkData
     */
    private static convertTabUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.tabSize || uiData.tabSize === 0) {
            sdkData.defTabSz = ptToEmu(uiData.tabSize);
        }
    }

    /**
     * 自定义制表位列表
     * @param uiData
     * @param sdkData
     */
    private static convertTabListUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.tabList && Array.isArray(uiData.tabList)) {
            const { tabList } = uiData;
            const tab = tabList.map(tab => ({
                pos: ptToEmu(tab.position),
                algn: invertMap(TEXT_TAB_ALIGN_TYPE_MAP).get(tab.alignment),
            }));
            sdkData.tabLst = sdkData.tabLst || {};
            sdkData.tabLst.tab = tab;
        }
    }

    /**
     * defaultRunProps
     * @param uiData
     * @param sdkData
     */
    private static convertDefaultRunPropsUiToSdk(
        uiData: DeepReadonly<DeepPartial<IParagraphStyle>>,
        sdkData: ICTTextParagraphProperties,
    ): void {
        if (uiData.defaultRunProps) {
            sdkData.defRPr = {};
            TextRunStyleConverter.convertPartialDataFromUiToSdk(uiData.defaultRunProps, sdkData.defRPr);
        }
    }
}
